<template>
  <div class="create">
    <div class="create-header">
      <ff-button :action="logout" :text="'Logout'" :options="{ backgroundColor: '#272727'}"/>
    </div>
    <div class="create-container">
      <ff-loading v-if="loading" :loading="loading" :text="'Architecting Your Channel'"/>
      <div v-if="!loading">
        <h1>Create a Channel</h1>
        <h2>Welcome to Fanfolk. This Channel will be the home for your content: your channel name, and domain. You can change everything else later.</h2>
        <form v-on:submit.prevent="submit">
          <ff-input
            id="name"
            class="is-dark"
            placeholder="Dr. Thompson's Tips"
            type="text"
            :label="'Channel Name'"
            v-model="name"/>
          <i v-if="nameError" class="error">{{nameError}}</i>
          <ff-input
            id="description"
            class="is-dark"
            placeholder="Tips and tricks for staying healthy"
            type="text"
            v-model="description"
            :label="'Short Description'"
            />
          <i v-if="descriptionError" class="error">{{descriptionError}}</i>
          <ff-input
            id="subdomain"
            class="is-dark"
            placeholder="Channel ID"
            type="text"
            maxlength="25"
            :action="subdomainChanged"
            :label="'Channel ID'"
            v-model="subdomain"/>
          <i v-if="!subdomainError && subdomain">Your home on the web will be {{subdomain}}.fanfolk.com</i>
          <i v-if="subdomainError" class="error">{{subdomainError}}</i>
          <ff-button type="submit" class="submit" :action="submit" :text="'Save Channel'"/>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { auth, db } from '../../helpers/firebase'
import FFButton from '../DesignSystem/FFButton'
import FFInput from '../DesignSystem/FFInput'
import FFLoading from '../DesignSystem/FFLoading'
import 'firebase/functions'
import email from '../../mixins/email'

const reservedChannels = [
  'about',
  'terms',
  'home',
  'signup',
  'register',
  'twitter',
  'facebook',
  'instagram',
  'google',
  'netflix',
  'substack',
  'dash',
  'dashboard',
  'contact',
  'privacy',
  'stripe',
]

export default {
  name: 'CreateChannel',
  props: {
  },
  components: {
    'ff-button': FFButton,
    'ff-input': FFInput,
    'ff-loading': FFLoading
  },
  mixins: [
    email
  ],
  data() {
    return {
      step: 'name',
      name: '',
      description: '',
      subdomain: '',
      nameError: '',
      descriptionError: '',
      subdomainError: '',
      subdomainValid: false,
      loading: false
    };
  },
  computed: {
    ...mapState(['user'])
  },
  mounted () {
    this.loading = true
    if (!this.user) return this.$router.push({name: 'signup'})
    let docRef = db.collection('users').doc(this.user.uid)
    docRef.get().then((userDoc) => {
      this.loading = false
      if (!userDoc.exists) return
      let data = userDoc.data()
      if (!data.channels) return
      if (data.channels && data.channels.length > 0) {
        return window.location.assign(`http://${data.channels[0]}.${process.env.VUE_APP_DOMAIN}`)
      }
    })
  },
  methods: {
    async next () {
      this.emailToAdmin({
        html: `New channel created by ${this.user.email} at ${this.subdomain}`,
        subject: `New channel created: ${this.subdomain}`
      })
      return this.$router.push({ name: 'complete', query: { subdomain: this.subdomain } })
    },
    submit () {
      if (this.name) {
        this.nameError = ''
      } else {
        this.nameError = 'You have to have a name for your channel.'
      }
      if (this.description) {
        this.descriptionError = ''
      } else {
        this.descriptionError = 'You have to have a description for your channel.'
      }
      if (this.descriptionError || this.nameError) return;

      if (this.subdomainValid) {
        this.loading = true;
        let docRef = db.collection('channels').doc(this.subdomain)
        docRef.get().then((doc) => {
          if (doc.exists) {
            this.subdomainError = 'Someone already has that ID reserved.'
            this.loading = false;
          } else {
            let uid = auth.currentUser.uid;
            db.collection('channels').doc(this.subdomain).set({
              owner: uid,
              name: this.name,
              description: this.description,
            }).then(() => {
              let userRef = db.collection('users').doc(uid)
              // this is set up as an array in case we ever want to allow
              // users to have multiple channels. For now, a set is safe because
              // we only allow one.
              userRef.set({
                channels: [this.subdomain]
              })
              this.next()
            })
          }
        })
      } else {
        this.subdomainError = 'You have to have a place for your channel to live.'
        this.loading = false;
      }
    },
    logout () {
      auth.signOut().then(() => {
        return this.$router.push({name: 'signup'})
      })
    },
    subdomainChanged () {
      if (reservedChannels.indexOf(this.subdomain) !== -1) {
        this.subdomainValid = false
        this.subdomainError = 'Sorry, we have that ID saved for a special purpose.'
        return;
      }

      let match = /[a-z]*/.exec(this.subdomain)[0].length;
      let subLength = this.subdomain.length;
      this.subdomainValid = subLength > 4 && match === subLength;
      if (!this.subdomainValid) {
        if (subLength > 4) {
          this.subdomainError = 'Channel IDs must be all lowercase and contain no symbols or spaces.'
        } else if (subLength > 0) {
          this.subdomainError = 'Channel IDs must be at least 5 characters long.'
        }
      } else {
        this.subdomainError = ''
      }
    }
  }
}
</script>

<style scoped lang="scss">
  @import "../../assets/styles/app.scss";

  .create {
    height: 100%;
    background: $color-dark;
    overflow: auto;
  }

  .create-container {
    @include container;
  }

  h1 {
    @include displayText;
    font-family: $fontFamily--creative;
    font-weight: 100;
    color: $color-white;
  }

  h2 {
    @include text('body');
    color: $color-white;
    margin-bottom: $unit-3;
  }

  i {
    font-size: $fontSize-small;
    margin-bottom: $unit-1;
    display: block;
    color: $color-white;
  }

  .error {
    color: $color-red;
  }

  .submit {
    margin-top: $unit-2;
  }

  .create-header {
    padding: 1rem;
  }
  @import "../../assets/styles/mobile.scss";

</style>
